import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Save, AlertCircle, Trash2, Clock, DollarSign, FileSearch } from 'lucide-react';
import { ProjectService } from '../services/projectService';
import { ProfitAnalysisService } from '../services/profitAnalysisService';
import { formatDate } from '../utils/dateFormatting';
import { Modal, ModalBody, ModalFooter, Button, Input, Dropdown } from './ui';
import './OperationsDashboardModal.css';

// Standardized modal with ModalBody and ModalFooter components
const OperationsDashboardModal = ({ project, clients, contacts, modelQuotes = [], isOpen, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    jobNumber: '',
    rev: '',
    contactId: '',
    clientId: '',
    modelingStatus: '',
    modelingState: '',
    billingStatus: '',
    agreementStatus: '',
    initialAcceptance: '',
    invoicedDate: '',
    paidDate: '',
    approximateAcreage: '',
    modelPrice: '',
    additionalInfo: '',
    additionalRequests: [],
    isLineworkOnly: false
  });
  const [calculatedModelPrice, setCalculatedModelPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const acreageUpdateTimeoutRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStateChangeConfirm, setShowStateChangeConfirm] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState(null);
  const [originalModelingState, setOriginalModelingState] = useState('');
  const [error, setError] = useState(null);
  const [manicTimeHours, setManicTimeHours] = useState(null);
  const [laborCost, setLaborCost] = useState(null);
  const [loadingManicTime, setLoadingManicTime] = useState(false);
  const [manicTimeError, setManicTimeError] = useState(null);

  // Fetch hours + labor cost for the project
  const fetchProjectCostData = useCallback(async (projectId) => {
    try {
      setLoadingManicTime(true);
      setManicTimeError(null);

      const { hours, laborCost: cost } = await ProfitAnalysisService.getProjectCostSummary(projectId);
      setManicTimeHours(hours);
      setLaborCost(cost);
    } catch (error) {
      console.error('Error fetching project cost data:', error);
      setManicTimeError(error.message);
      setManicTimeHours(null);
      setLaborCost(null);
    } finally {
      setLoadingManicTime(false);
    }
  }, []);

  // Update acreage in DB immediately (with short debounce) and refetch price
  const updateAcreageAndRefetchPrice = useCallback(async (projectId, acreage) => {
    if (!projectId) return;

    try {
      setLoadingPrice(true);
      // Update acreage in database
      await ProjectService.updateProject(projectId, 'approximate_acreage', acreage);
      // Fetch the recalculated price from the view
      const data = await ProjectService.getProjectWithCalculatedPrice(projectId);
      if (data?.calculated_model_price !== null && data?.calculated_model_price !== undefined) {
        setCalculatedModelPrice(data.calculated_model_price);
      }
    } catch (error) {
      console.error('Error updating acreage:', error);
    } finally {
      setLoadingPrice(false);
    }
  }, []);

  useEffect(() => {
    if (project) {
      console.log('Modal: Updating form data with project:', {
        id: project.id,
        approximateAcreage: project.approximateAcreage
      });
      
      setFormData({
        jobNumber: project.jobNumber || '',
        rev: project.rev || '',
        contactId: project.contactId || '',
        clientId: project.clientId || '',
        modelingStatus: project.modelingStatus || '',
        modelingState: project.modelingState || '',
        billingStatus: project.billingStatus || '',
        agreementStatus: project.agreementStatus || '',
        initialAcceptance: project.initialAcceptance ? formatDate(project.initialAcceptance, 'input') : '',
        invoicedDate: project.invoicedDate ? formatDate(project.invoicedDate, 'input') : '',
        paidDate: project.paidDate ? formatDate(project.paidDate, 'input') : '',
        approximateAcreage: project.approximateAcreage || '',
        modelPrice: project.modelPrice || '',
        additionalInfo: project.modelRequestAdditionalInfo || '',
        additionalRequests: project.additionalRequests || [],
        isLineworkOnly: project.isLineworkOnly || false
      });
      
      // Fetch calculated price from the view
      fetchCalculatedPrice(project.id);

      // Fetch hours + labor cost for all projects
      fetchProjectCostData(project.id);

      // Store original modeling state for comparison
      setOriginalModelingState(project.modelingState || '');
    }

    // Cleanup timeout on unmount or project change
    return () => {
      if (acreageUpdateTimeoutRef.current) {
        clearTimeout(acreageUpdateTimeoutRef.current);
      }
    };
  }, [project, project?.approximateAcreage, fetchProjectCostData]);

  // Token-based similarity (Dice coefficient over bigrams + token overlap)
  const computeSimilarity = (a, b) => {
    const normalize = (s) => (s || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const an = normalize(a);
    const bn = normalize(b);
    if (!an || !bn) return 0;
    if (an === bn) return 1;

    // Token Jaccard
    const aTokens = new Set(an.split(' ').filter(t => t.length >= 2));
    const bTokens = new Set(bn.split(' ').filter(t => t.length >= 2));
    const tokenIntersect = [...aTokens].filter(t => bTokens.has(t)).length;
    const tokenUnion = new Set([...aTokens, ...bTokens]).size;
    const jaccard = tokenUnion ? tokenIntersect / tokenUnion : 0;

    // Bigram Dice
    const bigrams = (s) => {
      const grams = new Set();
      for (let i = 0; i < s.length - 1; i++) grams.add(s.slice(i, i + 2));
      return grams;
    };
    const aBg = bigrams(an.replace(/\s/g, ''));
    const bBg = bigrams(bn.replace(/\s/g, ''));
    const bgIntersect = [...aBg].filter(g => bBg.has(g)).length;
    const dice = (aBg.size + bBg.size) ? (2 * bgIntersect) / (aBg.size + bBg.size) : 0;

    return Math.max(jaccard, dice);
  };

  // Find best fuzzy-matching model quote for this client
  const suggestedQuote = useMemo(() => {
    if (!project?.clientId || !project?.projectName || !modelQuotes?.length) return null;

    const candidates = modelQuotes.filter(q => q.client_id === project.clientId && q.project_name);
    if (!candidates.length) return null;

    let best = null;
    let bestScore = 0;
    for (const q of candidates) {
      const score = computeSimilarity(project.projectName, q.project_name);
      if (score > bestScore) {
        bestScore = score;
        best = q;
      }
    }

    return bestScore >= 0.4 ? { quote: best, score: bestScore } : null;
  }, [project?.clientId, project?.projectName, modelQuotes]);

  const applySuggestedPrice = () => {
    if (!suggestedQuote?.quote?.price) return;
    handleInputChange('modelPrice', String(Math.round(parseFloat(suggestedQuote.quote.price))));
  };

  const fetchCalculatedPrice = async (projectId) => {
    console.log('🧮 Fetching calculated price for project:', projectId);
    const data = await ProjectService.getProjectWithCalculatedPrice(projectId);
    console.log('🧮 Calculated price result:', data);
    
    if (data && data.calculated_model_price !== null && data.calculated_model_price !== undefined) {
      console.log('✅ Setting calculated price:', data.calculated_model_price);
      setCalculatedModelPrice(data.calculated_model_price);
    } else {
      console.log('❌ No calculated price available');
      setCalculatedModelPrice(null);
    }
  };

  const handleInputChange = (field, value) => {
    // Handle modeling state change with confirmation
    if (field === 'modelingState' && value !== originalModelingState && value !== '') {
      setPendingStateChange(value);
      setShowStateChangeConfirm(true);
      return; // Don't update yet, wait for confirmation
    }

    const newFormData = {
      ...formData,
      [field]: value
    };

    setFormData(newFormData);

    // When acreage changes, debounce update to DB and refetch calculated price
    if (field === 'approximateAcreage' && project?.id) {
      // Clear any pending timeout
      if (acreageUpdateTimeoutRef.current) {
        clearTimeout(acreageUpdateTimeoutRef.current);
      }

      const acreageValue = value && !isNaN(parseFloat(value)) ? parseFloat(value) : null;

      // Debounce: update DB after 300ms of no typing
      acreageUpdateTimeoutRef.current = setTimeout(() => {
        updateAcreageAndRefetchPrice(project.id, acreageValue);
      }, 300);
    }
  };

  const confirmStateChange = () => {
    setFormData({
      ...formData,
      modelingState: pendingStateChange
    });
    setShowStateChangeConfirm(false);
    setPendingStateChange(null);
  };

  const cancelStateChange = () => {
    setShowStateChangeConfirm(false);
    setPendingStateChange(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        job_number: formData.jobNumber || null,
        rev: formData.rev ? parseInt(formData.rev) : null,
        contact_id: formData.contactId || null,
        modeling_status: formData.modelingStatus || null,
        modeling_state: formData.modelingState || null,
        model_price: formData.modelPrice && !isNaN(parseFloat(formData.modelPrice))
          ? Math.round(parseFloat(formData.modelPrice))
          : null,
        billing_status: formData.billingStatus || null,
        agreement_status: formData.agreementStatus || null,
        initial_acceptance: formData.initialAcceptance || null,
        invoiced_date: formData.invoicedDate || null,
        paid_date: formData.paidDate || null,
        model_request_additional_info: formData.additionalInfo || null,
        approximate_acreage: formData.approximateAcreage && !isNaN(parseFloat(formData.approximateAcreage))
          ? parseFloat(formData.approximateAcreage)
          : null,
        additional_requests: formData.additionalRequests.length > 0 ? formData.additionalRequests : null
      };

      console.log('📝 Fields to update:', fieldsToUpdate);

      for (const [field, value] of Object.entries(fieldsToUpdate)) {
        const currentFieldName = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        
        // Skip updating critical calculation fields to null if they currently have values
        if (field === 'model_price' && value === null && project[currentFieldName]) {
          console.log('⚠️ Skipping model_price update to null - keeping existing value:', project[currentFieldName]);
          continue;
        }
        if (field === 'approximate_acreage' && value === null && project[currentFieldName]) {
          console.log('⚠️ Skipping approximate_acreage update to null - keeping existing value:', project[currentFieldName]);
          continue;
        }
        
        if (value !== project[currentFieldName]) {
          console.log(`🔄 Updating ${field} from ${project[currentFieldName]} to ${value}`);
          await ProjectService.updateProject(project.id, field, value);
        }
      }

      if (onSave) {
        await onSave();
      }
      
      // Auto-close modal after successful save
      onClose();
      
      // Refresh calculated price after save (always, since it depends on multiple fields)
      await fetchCalculatedPrice(project.id);
      
      // Keep the form data as is since the user may want to continue editing
      // The useEffect will update if the project prop changes
      setError(null);
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      
      await ProjectService.deleteProject(project.id);
      
      if (onDelete) {
        await onDelete(project.id);
      }
      
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const modelingStatusOptions = [
    'Startup', 'Data Prep', 'Ready to Model', 'Modeling',
    'Double Check', 'Corrections', 'Review',
    'Ready to Export', 'Completed', 'Utilities'
  ].map(status => ({ value: status, label: status }));

  const modelingStateOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Hold', label: 'Hold' },
    { value: 'Completed', label: 'Completed' }
  ];

  const billingStatusOptions = ['Quoted', 'Invoiced', 'Paid', 'Voided'].map(status => ({ value: status, label: status }));

  const agreementStatusOptions = [
    'Ready', 'Utility Ready', 'Sent', 'Completed',
    'None needed', 'Revision-none', 'Voided', 'Hold', 'Not Signed'
  ].map(status => ({ value: status, label: status }));

  const additionalRequestOptions = [
    '3D/Elevated Pipe',
    'Initial Erosion Surface',
    'Advanced Utility Model'
  ];

  const clientOptions = clients.map(client => ({ value: client.id, label: client.name }));

  if (!isOpen) return null;

  return (
    <>
      <Modal trackAs="OperationsDashboardModal" isOpen={isOpen} onClose={onClose} size="xl" showCloseButton={false}>
        {/* Custom Header with Project Info */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            {/* Job Number and Project Name on one line */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                <span
                  style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-400)',
                    flexShrink: 0,
                  }}
                >
                  {formData.jobNumber && formData.rev && parseInt(formData.rev) > 0
                    ? `${formData.jobNumber} Rev${formData.rev}`
                    : formData.jobNumber || 'N/A'}
                </span>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-50)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {project?.projectName || 'Project Details'}
                </h1>
              </div>
            </div>

            {/* Hours + Labor Cost Badge */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--dark-elevated)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--gray-600)',
                flexShrink: 0,
              }}
            >
              {loadingManicTime ? (
                <>
                  <div style={{ width: '14px', height: '14px', border: '2px solid var(--gray-600)', borderTopColor: 'var(--cta-blue)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>Loading...</span>
                </>
              ) : manicTimeError ? (
                <>
                  <AlertCircle size={14} style={{ color: 'var(--danger)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>Error</span>
                </>
              ) : (
                <>
                  <Clock size={14} style={{ color: 'var(--gray-400)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    {(manicTimeHours ?? 0).toFixed(2)} hrs
                  </span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-500)' }}>•</span>
                  <DollarSign size={14} style={{ color: 'var(--gray-400)' }} />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    ${(laborCost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 'var(--space-2)',
                cursor: 'pointer',
                color: 'var(--gray-400)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-md)',
                transition: 'all var(--transition-fast)',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--gray-700)';
                e.currentTarget.style.color = 'var(--gray-50)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--gray-400)';
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <ModalBody>
          {error && (
            <div className="modal-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* LEFT COLUMN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {/* PROJECT INFORMATION SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Project Information
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  <Input
                    label="Job Number"
                    value={formData.jobNumber}
                    onChange={(e) => handleInputChange('jobNumber', e.target.value)}
                    placeholder="Enter job number"
                  />

                  <Input
                    label="Revision"
                    type="number"
                    value={formData.rev}
                    onChange={(e) => handleInputChange('rev', e.target.value)}
                    placeholder="0"
                  />

                  <Dropdown
                    label="Client"
                    options={clientOptions}
                    value={formData.clientId}
                    onChange={(value) => handleInputChange('clientId', value)}
                    disabled
                    placeholder="Select client"
                  />
                </div>
              </div>

              {/* WORKFLOW STATUS SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Workflow Status
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  <Dropdown
                    label="Modeling Status"
                    options={modelingStatusOptions}
                    value={formData.modelingStatus}
                    onChange={(value) => handleInputChange('modelingStatus', value)}
                    placeholder="Select status"
                  />

                  <Dropdown
                    label="Modeling State"
                    options={modelingStateOptions}
                    value={formData.modelingState}
                    onChange={(value) => handleInputChange('modelingState', value)}
                    placeholder="Select state"
                  />
                </div>
              </div>

              {/* DATES & MILESTONES SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Dates & Milestones
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  <Input
                    label="Initial Acceptance"
                    type="date"
                    value={formData.initialAcceptance}
                    onChange={(e) => handleInputChange('initialAcceptance', e.target.value)}
                  />

                  <Input
                    label="Invoice Date"
                    type="date"
                    value={formData.invoicedDate}
                    onChange={(e) => handleInputChange('invoicedDate', e.target.value)}
                  />

                  <Input
                    label="Paid Date"
                    type="date"
                    value={formData.paidDate}
                    onChange={(e) => handleInputChange('paidDate', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              {/* FINANCIAL & BILLING SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Financial & Billing
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                  <Dropdown
                    label="Billing Status"
                    options={billingStatusOptions}
                    value={formData.billingStatus}
                    onChange={(value) => handleInputChange('billingStatus', value)}
                    placeholder="Select status"
                  />

                  <Dropdown
                    label="Agreement Status"
                    options={agreementStatusOptions}
                    value={formData.agreementStatus}
                    onChange={(value) => handleInputChange('agreementStatus', value)}
                    placeholder="Select status"
                  />

                  <Input
                    label="Approximate Acreage"
                    type="number"
                    step="0.01"
                    value={formData.approximateAcreage}
                    onChange={(e) => handleInputChange('approximateAcreage', e.target.value)}
                    placeholder="0.00"
                  />

                  <div className="modal-field">
                    <label>Calculated Model Price</label>
                    <div className="calculated-field">
                      {loadingPrice ? 'Calculating...'
                        : calculatedModelPrice !== null && calculatedModelPrice !== undefined
                          ? `$${Math.round(parseFloat(calculatedModelPrice)).toLocaleString()}`
                          : 'N/A'}
                    </div>
                  </div>

                  <Input
                    label="Model Price ($)"
                    type="number"
                    step="1"
                    value={formData.modelPrice}
                    onChange={(e) => handleInputChange('modelPrice', e.target.value)}
                    placeholder="0"
                  />

                  {suggestedQuote && (
                    <div
                      style={{
                        padding: 'var(--space-3)',
                        background: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid var(--cta-blue)',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <FileSearch size={14} style={{ color: 'var(--cta-blue)', flexShrink: 0 }} />
                        <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--cta-blue)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Possible matching quote
                        </span>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', marginLeft: 'auto' }}>
                          {Math.round(suggestedQuote.score * 100)}% match
                        </span>
                      </div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-100)', fontWeight: 'var(--font-medium)' }}>
                        {suggestedQuote.quote.project_name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-300)' }}>
                          {suggestedQuote.quote.price != null
                            ? `$${Math.round(parseFloat(suggestedQuote.quote.price)).toLocaleString()}`
                            : 'No price'}
                          {suggestedQuote.quote.quote_status && (
                            <span style={{ marginLeft: 'var(--space-2)', color: 'var(--gray-400)' }}>
                              • {suggestedQuote.quote.quote_status}
                            </span>
                          )}
                        </div>
                        {suggestedQuote.quote.price != null && (
                          <button
                            type="button"
                            onClick={applySuggestedPrice}
                            style={{
                              background: 'var(--cta-blue)',
                              color: 'white',
                              border: 'none',
                              padding: 'var(--space-1) var(--space-3)',
                              borderRadius: 'var(--radius-md)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--font-semibold)',
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          >
                            Use Price
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ADDITIONAL REQUESTS SECTION */}
              <div>
                <h2 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}>
                  Additional Requests
                </h2>
                <div style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 'var(--space-2)',
                  }}>
                    {/* Linework Only indicator */}
                    {formData.isLineworkOnly && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: 'var(--space-2)',
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          border: '1px solid var(--warning, #f59e0b)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--warning, #f59e0b)',
                          fontWeight: 'var(--font-medium)',
                        }}>
                          Linework Only
                        </span>
                      </div>
                    )}
                    {additionalRequestOptions.map(option => {
                      const isChecked = formData.additionalRequests.includes(option);
                      const itemId = `request-${option.replace(/[^a-zA-Z0-9]/g, '-')}`;

                      return (
                        <label
                          key={option}
                          htmlFor={itemId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 'var(--space-2)',
                            backgroundColor: isChecked ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                            border: `1px solid ${isChecked ? 'var(--cta-blue)' : 'var(--gray-600)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            if (!isChecked) {
                              e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                              e.currentTarget.style.borderColor = 'var(--gray-500)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChecked) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = 'var(--gray-600)';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            id={itemId}
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                handleInputChange('additionalRequests', [...formData.additionalRequests, option]);
                              } else {
                                handleInputChange('additionalRequests',
                                  formData.additionalRequests.filter(item => item !== option)
                                );
                              }
                            }}
                            style={{ marginRight: 'var(--space-2)' }}
                          />
                          <span style={{
                            fontSize: 'var(--text-sm)',
                            color: isChecked ? 'var(--gray-50)' : 'var(--gray-300)',
                            fontWeight: isChecked ? 'var(--font-medium)' : 'normal',
                          }}>
                            {option}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Selection summary */}
                <div style={{
                  marginTop: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}>
                  {(formData.additionalRequests.length > 0 || formData.isLineworkOnly)
                    ? `${formData.additionalRequests.length + (formData.isLineworkOnly ? 1 : 0)} selected`
                    : "None selected"}
                </div>
              </div>
            </div>
          </div>

          {/* MODEL REQUEST ADDITIONAL INFO SECTION - Full width below columns */}
          {formData.additionalInfo && (
            <div style={{ marginTop: 'var(--space-6)' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}
              >
                Model Request Additional Info
              </label>
              <div
                style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-200)',
                  fontSize: 'var(--text-sm)',
                  minHeight: '80px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {formData.additionalInfo || "No additional information provided"}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving || deleting}
            >
              <Trash2 size={16} />
              Delete Project
            </Button>
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || deleting}
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <h3>Delete Project</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowDeleteConfirm(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="delete-confirm-body">
              <p>Are you sure you want to delete this project?</p>
              <p><strong>{project?.projectName || 'Unnamed Project'}</strong></p>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="delete-confirm-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete-confirm"
                onClick={handleDelete}
                disabled={deleting}
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modeling State Change Confirmation Dialog */}
      {showStateChangeConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelStateChange}>
          <div className="delete-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <h3>Confirm State Change</h3>
              <button
                className="modal-close-btn"
                onClick={cancelStateChange}
              >
                <X size={20} />
              </button>
            </div>
            <div className="delete-confirm-body">
              <p>Are you sure you want to change the modeling state?</p>
              <p>
                <strong>From:</strong> {originalModelingState || 'None'} <br />
                <strong>To:</strong> {pendingStateChange}
              </p>
            </div>
            <div className="delete-confirm-footer">
              <button
                className="btn-cancel"
                onClick={cancelStateChange}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={confirmStateChange}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OperationsDashboardModal;